import { useEffect, useRef, useState } from "react";

export type BackgroundEffect = "none" | "blur" | "blur-strong";

const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite";

const BLUR_PX: Record<Exclude<BackgroundEffect, "none">, number> = {
  blur: 10,
  "blur-strong": 22,
};

type Status = "idle" | "loading" | "active" | "error";

/**
 * Runs on-device selfie segmentation so only the background is blurred.
 * Draws the composited frames into the supplied canvas.
 */
export const useBackgroundBlur = (
  videoRef: React.RefObject<HTMLVideoElement>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  effect: BackgroundEffect,
  enabled: boolean,
) => {
  const [status, setStatus] = useState<Status>("idle");
  // Selfie models differ on which value marks the person; detect it once.
  const invertRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!enabled || effect === "none") {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    let raf = 0;
    let segmenter: { segmentForVideo: Function; close: () => void } | null = null;
    let lastTimestamp = -1;

    const maskCanvas = document.createElement("canvas");
    const personCanvas = document.createElement("canvas");

    const run = async () => {
      setStatus("loading");
      try {
        const { FilesetResolver, ImageSegmenter } = await import("@mediapipe/tasks-vision");
        const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
        if (cancelled) return;
        segmenter = await ImageSegmenter.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO",
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        });
        if (cancelled) {
          segmenter?.close();
          return;
        }
        setStatus("active");
        raf = requestAnimationFrame(tick);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || !segmenter) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h || video.readyState < 2) return;

      const ts = performance.now();
      if (ts - lastTimestamp < 30) return;
      lastTimestamp = ts;

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        personCanvas.width = w;
        personCanvas.height = h;
      }

      try {
        segmenter.segmentForVideo(video, ts, (result: any) => {
          const mask = result?.confidenceMasks?.[0];
          if (!mask) return;
          const mw = mask.width;
          const mh = mask.height;
          const values: Float32Array = mask.getAsFloat32Array();

          if (invertRef.current === null) {
            // Sample the centre (likely person) against the top corners (likely background).
            const at = (x: number, y: number) => values[Math.floor(y) * mw + Math.floor(x)] ?? 0;
            const centre = at(mw / 2, mh * 0.6);
            const corners = (at(2, 2) + at(mw - 3, 2)) / 2;
            invertRef.current = centre < corners;
          }
          const invert = invertRef.current;

          if (maskCanvas.width !== mw || maskCanvas.height !== mh) {
            maskCanvas.width = mw;
            maskCanvas.height = mh;
          }
          const maskCtx = maskCanvas.getContext("2d");
          const personCtx = personCanvas.getContext("2d");
          const ctx = canvas.getContext("2d");
          if (!maskCtx || !personCtx || !ctx) return;

          const image = maskCtx.createImageData(mw, mh);
          const data = image.data;
          for (let i = 0; i < values.length; i += 1) {
            const v = invert ? 1 - values[i] : values[i];
            data[i * 4 + 3] = v * 255;
          }
          maskCtx.putImageData(image, 0, 0);

          // Sharp subject cut out with the mask.
          personCtx.clearRect(0, 0, w, h);
          personCtx.drawImage(video, 0, 0, w, h);
          personCtx.globalCompositeOperation = "destination-in";
          personCtx.drawImage(maskCanvas, 0, 0, w, h);
          personCtx.globalCompositeOperation = "source-over";

          // Blurred background, slightly overscanned so edges stay filled.
          const blur = BLUR_PX[effect as Exclude<BackgroundEffect, "none">] ?? 10;
          ctx.clearRect(0, 0, w, h);
          ctx.filter = `blur(${blur}px)`;
          const pad = blur * 2;
          ctx.drawImage(video, -pad, -pad, w + pad * 2, h + pad * 2);
          ctx.filter = "none";
          ctx.drawImage(personCanvas, 0, 0, w, h);

          mask.close?.();
        });
      } catch {
        /* skip this frame */
      }
    };

    run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      segmenter?.close();
    };
  }, [videoRef, canvasRef, effect, enabled]);

  return status;
};
